import { useEffect, useState } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { isDemoMode } from "../../lib/api";

export function RequireAuth({ admin = false }: { admin?: boolean }) {
  const location = useLocation();
  const [state, setState] = useState<"loading" | "allowed" | "blocked">("loading");

  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    const timeoutMs = 8000;
    const complete = (nextState: "allowed" | "blocked") => {
      if (!active) return;
      active = false;
      window.clearTimeout(timeoutId);
      setState(nextState);
    };
    const timeoutId = window.setTimeout(() => {
      controller.abort();
      complete("blocked");
    }, timeoutMs);

    const check = async () => {
      // In local development we allow an explicit demo-auth flag to skip the network session check.
      // Production never sees this branch because import.meta.env.DEV is compile-time false there.
      const demoAuth = import.meta.env.DEV ? localStorage.getItem("ktd_demo_auth") : isDemoMode ? localStorage.getItem("ktd_demo_auth") : null;
      if (demoAuth === (admin ? "admin" : "client")) {
        complete("allowed");
        return;
      }
      try {
        const response = await fetch("/api/auth/session", {
          credentials: "include",
          signal: controller.signal,
        });
        if (!response.ok) {
          throw new Error(`Session request failed with ${response.status}.`);
        }
        const data = (await response.json()) as {
          user?: { email?: string; name?: string; role?: string } | null;
        };
        const role = typeof data?.user?.role === "string" ? data.user.role : "";
        const internalRoles = ["kreatethedate_admin", "admin", "manager", "supervisor", "team_member", "viewer"];
        const allowed = admin
          ? internalRoles.includes(role)
          : role === "client" || role === "viewer" || role === "kreatethedate_admin";
        complete(allowed ? "allowed" : "blocked");
      } catch {
        complete("blocked");
      }
    };
    void check();
    return () => {
      active = false;
      controller.abort();
      window.clearTimeout(timeoutId);
    };
  }, [admin]);

  if (state === "loading") {
    return (
      <div className="grid min-h-screen place-items-center bg-ktd-black text-white">
        <div className="luxe-card rounded-lg p-8 text-center">
          <p className="font-display text-3xl font-bold gold-text">Checking admin session...</p>
          <p className="mt-3 text-sm text-white/62">Checking secure access...</p>
        </div>
      </div>
    );
  }

  if (state === "blocked") {
    return <Navigate to={admin ? "/admin/login" : "/login"} replace state={{ from: location }} />;
  }

  return <Outlet />;
}
